<template>
  <div class="reports-view">
    <Header :title="$route.meta.title" :buttons="[]">
      <v-btn
        class="px-4"
        color="primary"
        outlined
        v-shortkey="exportShortkeys"
        @shortkey.native="exportReport"
        @click="exportReport"
      >
        {{ getLabelWithShortkey('Eksportuj do csv', exportShortkeys.key) }}
      </v-btn>
    </Header>

    <div class="filters">
      <div class="filters__inputs d-flex justify-space-between">
        <v-autocomplete
          v-model="driverId"
          class="driver-settlements__driver-select"
          :items="driverOptions"
          item-text="name"
          item-value="id"
          label="Kierowca"
          placeholder="Wybierz kierowcę"
          :loading="isLoadingDrivers"
          no-data-text="Brak kierowców w wybranym miesiącu"
          outlined
          hide-details
          append-icon="mdi-chevron-down"
          dense
        />
        <div class="d-flex align-center">
          <MonthPicker v-model="month" />
          <v-btn
            class="px-4 ml-2"
            color="primary"
            outlined
            v-shortkey="toggleStatsShortkeys"
            @shortkey.native="showStats = !showStats"
            @click="showStats = !showStats"
          >
            {{ getLabelWithShortkey(showStats ? 'Ukryj statystyki' : 'Pokaż statystyki', toggleStatsShortkeys.key) }}
          </v-btn>
        </div>
      </div>
    </div>

    <DriverSettlementStats v-if="showStats" :items="stats" />

    <div class="pb-4">
      <v-data-table
        v-if="!weeks.length"
        class="driver-settlements__table"
        :headers="headers"
        :items="[]"
        :loading="isProcessing"
        hide-default-footer
        no-data-text="Brak wyników"
      />
      <div v-for="week in weeks" :key="week.id">
        <v-data-table
          class="driver-settlements__table"
          :headers="headers"
          :items="week.days"
          :items-per-page="-1"
          :loading="isProcessing"
          item-key="id"
          :item-class="() => 'table__row'"
          hide-default-footer
          @contextmenu:row="handleContextMenu"
        >
          <template #[`item.bonuses`]="{ item }">
            <v-tooltip v-if="item.bonus && item.bonus.note" bottom>
              <template #activator="{ on }">
                <span v-on="on">{{ item.bonuses }}</span>
              </template>
              <span>{{ item.bonus.note }}</span>
            </v-tooltip>
            <span v-else>{{ item.bonuses }}</span>
          </template>
        </v-data-table>
        <div class="driver-settlements__week-summary">
          <span>Podsumowanie tygodnia</span>
          <span>Średnia: {{ week.totals.avgWorkTime }}</span>
          <span>Suma: {{ week.totals.sumDrivingTime }}</span>
          <span>Suma: {{ week.totals.sumDistanceKm }}</span>
          <span>Suma: {{ week.totals.sumTrailerLoopCount }}</span>
          <span>Suma: {{ week.totals.sumSoloLoopCount }}</span>
          <span>Suma: {{ week.totals.sumBonusAmount }}</span>
          <span>Kilometry / pętle: {{ week.totals.kmPerLoop }}</span>
          <span>Czas jazdy / pętle: {{ week.totals.drivingTimePerLoop }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DateTime from 'luxon/src/datetime'
import { mapActions, mapGetters, mapState } from 'vuex'
import Header from '../../components/Layout/Header'
import MonthPicker from '../../components/Filters/MonthPicker'
import DriverSettlementStats from '../../components/Reports/DriverSettlementStats'
import exportRowsToCsv from '../../utils/exportRowsToCsv'
import { exportShortkeys, toggleStatsShortkeys, getLabelWithShortkey } from '../../const/shortKeys'

const BONUS_PERMISSION = 'feature-manage-driver-bonuses'

const DETAILS_HEADERS = [
  { text: 'Dzień', value: 'weekday', width: '12%' },
  { text: 'Data', value: 'formattedDate', width: '9%' },
  { text: 'Czas pracy', value: 'workTime', width: '10%' },
  { text: 'Czas jazdy', value: 'driveTime', width: '8%' },
  { text: 'Przejechane', value: 'distance', width: '8%' },
  { text: 'Pętle z przyczepą', value: 'loopsWithTrailer', width: '9%' },
  { text: 'Pętle solo', value: 'loopsSolo', width: '10%' },
  { text: 'Bonusy', value: 'bonuses', width: '8%' },
  { text: 'Kilometry / pętle', value: 'kmPerLoop', width: '11%' },
  { text: 'Czas jazdy / pętle', value: 'driveTimePerLoop', width: '15%' }
]

export default {
  components: {
    Header,
    MonthPicker,
    DriverSettlementStats
  },
  data: () => ({
    month: DateTime.local().toFormat('yyyy-MM'),
    driverId: null,
    showStats: true,
    isLoadingDrivers: false,
    headers: DETAILS_HEADERS.map(header => ({ ...header, sortable: false })),
    exportShortkeys,
    toggleStatsShortkeys
  }),
  computed: {
    ...mapState({
      weeks: state => state.driversDetails.weeks,
      stats: state => state.driversDetails.summary || [],
      isProcessing: state => state.driversDetails.isProcessing,
      driverOptions: state => state.driversDetails.drivers
    }),
    selectedDriverName() {
      return this.driverOptions.find(driver => driver.id === this.driverId)?.name
    },
    ...mapGetters({
      userPermissions: 'auth/getUserPermissions'
    }),
    canManageBonuses() {
      return this.userPermissions.some(permission => permission.name === BONUS_PERMISSION)
    }
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler(query) {
        const driverId = Number(query.driverId)
        if (driverId && driverId !== this.driverId) this.driverId = driverId
        if (query.month && /^\d{4}-\d{2}$/.test(query.month) && query.month !== this.month) {
          this.month = query.month
        }
      }
    },
    month: {
      immediate: true,
      handler() {
        this.fetchDriverOptions()
      }
    },
    driverId() {
      // while drivers are loading, report is fetched after driver is validated against the new list
      if (!this.isLoadingDrivers) this.fetchReport()
    }
  },
  methods: {
    ...mapActions({
      getItems: 'driversDetails/getItems',
      getDrivers: 'driversDetails/getDrivers',
      clearReport: 'driversDetails/clearReport',
      deleteBonus: 'driversDetails/deleteBonus',
      setContextMenu: 'layout/setContextMenu'
    }),
    getLabelWithShortkey,
    handleContextMenu(event, { item }) {
      event.preventDefault()
      if (!this.canManageBonuses) return

      const bonusForm = {
        component: 'DriverBonusForm',
        target: 'dialog',
        permission: BONUS_PERMISSION,
        props: { driverId: this.driverId, month: this.month }
      }
      const bonusActions = [
        { ...bonusForm, name: 'editBonus', text: 'Edytuj bonus' },
        { name: 'deleteBonus', text: 'Usuń bonus', target: 'function', permission: BONUS_PERMISSION, callback: this.removeBonus }
      ]

      const actions = item.bonus
        ? bonusActions
        : [{ ...bonusForm, name: 'addBonus', text: 'Dodaj bonus' }]
      this.setContextMenu({ actions, item, position: { x: event.clientX, y: event.clientY } })
    },
    fetchDriverOptions() {
      this.isLoadingDrivers = true
      this.getDrivers({ month: this.month })
        .then(() => {
          // previously selected driver may not be reported in the new month - fallback to the first one
          const isSelectedDriverAvailable = this.driverOptions.some(driver => driver.id === this.driverId)
          if (!isSelectedDriverAvailable) this.driverId = this.driverOptions[0]?.id ?? null
        })
        .finally(() => {
          this.isLoadingDrivers = false
          this.fetchReport()
        })
    },
    fetchReport() {
      if (!this.driverId) return this.clearReport()
      this.getItems({ driverId: this.driverId, month: this.month })
    },
    removeBonus(item) {
      this.deleteBonus({
        id: item.bonus.id,
        driverId: this.driverId,
        month: this.month
      })
    },
    exportReport() {
      const rows = this.weeks.flatMap(week => [
        ...week.days,
        {
          weekday: 'Podsumowanie tygodnia',
          formattedDate: '',
          workTime: `Średnia: ${week.totals.avgWorkTime}`,
          driveTime: `Suma: ${week.totals.sumDrivingTime}`,
          distance: `Suma: ${week.totals.sumDistanceKm}`,
          loopsWithTrailer: `Suma: ${week.totals.sumTrailerLoopCount}`,
          loopsSolo: `Suma: ${week.totals.sumSoloLoopCount}`,
          bonuses: `Suma: ${week.totals.sumBonusAmount}`,
          kmPerLoop: week.totals.kmPerLoop,
          driveTimePerLoop: week.totals.drivingTimePerLoop
        }
      ])
      exportRowsToCsv(
        this.headers,
        rows,
        `kierowcy-szczegolowo-${this.selectedDriverName || this.driverId}-${this.month}.csv`,
        this.stats
      )
    }
  }
}
</script>
